import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ISelf } from '@shared/models/api/base';
import { Observable, retry } from 'rxjs';
import { SessionStorageService } from './storage.service';
import { IAccount, UserService } from './user.service';

@Injectable({
	providedIn: 'root',
})
export class CrudService {
	public constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {}

	private headers(account?: IAccount): HttpHeaders {
		const accountId: string = account ? account.id : this.userService.account.id;
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({ Authorization: `Bearer ${token}`, 'x-account-id': accountId });
	}

	public get<ResponseType>(path: string, account?: IAccount): Observable<ResponseType> {
		const apiUrl = environment.awsEnvUri + path;
		return this.http
			.get<ResponseType>(apiUrl, {
				headers: this.headers(account),
			})
			.pipe(retry(1));
	}

	public create<CreateType, ResponseType>(value: CreateType, path: string): Observable<ResponseType> {
		const apiUrl = environment.awsEnvUri + path;
		return this.http
			.post<ResponseType>(apiUrl, value, {
				headers: this.headers(),
			})
			.pipe(retry(1));
	}

	// Type guard to check if valueOrPath is an ISelf object
	isSelfObject(value: any): value is ISelf {
		return typeof value === 'object' && 'self' in value && typeof value.self === 'string';
	}

	// Helper function to determine the API URL
	private getApiUrl(valueOrPath: { self?: string } | string, path?: string): string {
		if (typeof valueOrPath === 'string' && valueOrPath.length > 0) {
			return environment.awsEnvUri + valueOrPath;
		} else if (this.isSelfObject(valueOrPath) && valueOrPath.self) {
			return environment.awsEnvUri + valueOrPath.self;
		} else if (path && path.length > 0) {
			return environment.awsEnvUri + path;
		} else {
			throw new Error('A valid path string or a value with a self property must be provided.');
		}
	}

	public update<ResponseType>(value: ISelf): Observable<ResponseType>;
	public update<UpdateType, ResponseType>(value: UpdateType, path: string): Observable<ResponseType>;

	public update<ResponseType>(value: { self?: string }, path: string | undefined = undefined): Observable<ResponseType> {
		const apiUrl = this.getApiUrl(value, path);
		return this.http
			.put<ResponseType>(apiUrl, value, {
				headers: this.headers(),
			})
			.pipe(retry(1));
	}

	public patch<ResponseType>(value: ISelf): Observable<ResponseType>;
	public patch<PatchType, ResponseType>(value: PatchType, path: string): Observable<ResponseType>;

	public patch<ResponseType>(value: { self?: string }, path: string | undefined = undefined): Observable<ResponseType> {
		const apiUrl = this.getApiUrl(value, path);
		return this.http
			.patch<ResponseType>(apiUrl, value, {
				headers: this.headers(),
			})
			.pipe(retry(1));
	}

	public delete(value: ISelf): Observable<object>;
	public delete(path: string): Observable<object>;

	public delete(valueOrPath: ISelf | string): Observable<object> {
		const apiUrl = this.getApiUrl(valueOrPath);
		return this.http
			.delete(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1));
	}
}
